import React, {ComponentType, createContext, useContext, useState} from 'react';
import {FormViewerHandle} from '@wix/form-viewer/widget';
import {useControllerProps} from '../../../../Widget/ControllerContext';

export type AdditionalDetailsDataContextType = {
  formRefById: Record<string, React.RefObject<FormViewerHandle>>;
  setFormRef: (id: string, formRef: React.RefObject<FormViewerHandle>) => void;
  removeFormRef: (id: string) => void;
  validateAllForms: () => Promise<boolean>;
  continueToNextStep: () => void;
};

export const AdditionalDetailsDataContext = createContext({} as AdditionalDetailsDataContextType);

export function withAdditionalDetailsData<T extends object>(Component: ComponentType<T>) {
  /* eslint-disable-next-line sonarjs/cognitive-complexity */
  return function Wrapper(props: T) {
    const [formRefById, setFormRefById] = useState<Record<string, React.RefObject<FormViewerHandle>>>({});

    const {
      formsStore: {goToNextStep},
    } = useControllerProps();

    const setFormRef = (id: string, formRef: React.RefObject<FormViewerHandle>) => {
      setFormRefById((prev) => ({...prev, [id]: formRef}));
    };

    const removeFormRef = (id: string) => {
      setFormRefById((prev) => {
        const {[id]: _, ...rest} = prev;
        return rest;
      });
    };

    const validateAllForms = async () => {
      const allFormsValid = await Promise.all(Object.values(formRefById).map((formRef) => formRef.current?.validate()));

      return !allFormsValid.some((isValid) => !isValid);
    };

    const continueToNextStep = () => {
      goToNextStep();
    };

    return (
      <AdditionalDetailsDataContext.Provider
        value={{
          formRefById,
          setFormRef,
          removeFormRef,
          validateAllForms,
          continueToNextStep,
        }}>
        <Component {...props} />
      </AdditionalDetailsDataContext.Provider>
    );
  };
}

export function useAdditionalDetailsData() {
  return useContext(AdditionalDetailsDataContext);
}
